import { useState, useEffect } from "react";
import { ArrowDownRight, ArrowRight, ArrowUpRight, BriefcaseBusiness, Check, ChevronDown, CircleDot, ExternalLink, GraduationCap, Layers3, LifeBuoy, Menu, Mic, Palette, Radar as RadarIcon, ShieldCheck, Sparkles, X } from "lucide-react";
import { Link, Routes, Route, useLocation } from "react-router-dom";
import { PhoneFrame } from "@/components/PhoneFrame";
import { OrlisLogo } from "@/components/OrlisLogo";
import { MotionRuntime } from "@/components/MotionRuntime";
import { faqItems, pricingPlans, screenshots, playStoreUrl } from "@/orlisAssets";
import "@/App.css";

const screen = (id) => screenshots.find((item) => item.id === id);

function MaskedTitle({ lines, as: Tag = "h2", className = "", testId }) {
  return (
    <Tag className={`masked-title ${className}`} data-testid={testId}>
      {lines.map((line, index) => (
        <span className="line-mask" key={`${line}-${index}`}>
          <span className="line-text" style={{ animationDelay: `${index * 0.12}s` }}>{line}</span>
        </span>
      ))}
    </Tag>
  );
}

function SectionIntro({ number, label, title, body, testId }) {
  return (
    <div className="section-intro" data-reveal>
      <div className="section-index"><span>{number}</span><span>{label}</span></div>
      <MaskedTitle lines={title} testId={testId} />
      <p>{body}</p>
    </div>
  );
}

function IconMark({ icon: Icon = CircleDot, tone = "blue" }) {
  return <span className={`icon-mark icon-mark-${tone}`}><Icon size={19} strokeWidth={1.8} /></span>;
}

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [["Product", "product"], ["Study", "study"], ["Create", "create"], ["Work", "work"], ["Pricing", "pricing"], ["FAQ", "faq"]];
  const navigate = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`} data-testid="oramy-header-nav">
      <div className="header-inner">
        <a className="header-brand" href="#top" data-testid="oramy-header-logo" aria-label="ORLIS AI home">
          <OrlisLogo size="micro" testId="oramy-header-logo-image" />
        </a>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {links.map(([label, id]) => <a href={`#${id}`} data-testid={`oramy-nav-${id}`} key={id}>{label}</a>)}
        </nav>
        <div className="header-actions">
          <a href={playStoreUrl} target="_blank" rel="noreferrer" className="button button-dark button-small header-cta" data-testid="oramy-header-cta-download">
            Get ORLIS <ArrowUpRight size={15} />
          </a>
          <button type="button" className="menu-toggle" data-testid="oramy-header-menu-toggle" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen((value) => !value)}>
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="mobile-menu" data-testid="oramy-mobile-menu">
          {links.map(([label, id]) => <button type="button" key={id} data-testid={`oramy-mobile-nav-${id}`} onClick={() => navigate(id)}>{label}<ArrowRight size={16} /></button>)}
          <a href={playStoreUrl} target="_blank" rel="noreferrer" className="mobile-menu-cta" data-testid="oramy-mobile-cta-download">Get ORLIS <ArrowUpRight size={16} /></a>
        </div>
      )}
    </header>
  );
}

function PhonePair({ screens: pair, onOpen, testPrefix, className = "" }) {
  return (
    <div className={`phone-pair ${className}`} data-reveal>
      {pair.map((item, index) => <PhoneFrame key={item.id} screen={item} size="regular" testId={`${testPrefix}-${index + 1}`} onOpen={() => onOpen(item)} />)}
    </div>
  );
}

function Hero({ onOpen }) {
  return (
    <section className="hero-section" id="top">
      <div className="hero-ambient" aria-hidden="true" />
      <div className="hero-grid page-width">
        <div className="hero-copy" data-reveal>
          <div className="eyebrow"><span className="eyebrow-dot" /> India ka apna AI app</div>
          <MaskedTitle as="h1" lines={["One AI.", "Everything you need."]} testId="oramy-hero-headline" />
          <p className="hero-lead" data-testid="oramy-hero-subheading">Study smarter. Create better. Work faster.</p>
          <p className="hero-description">Chat, learn, create and get work done with powerful AI tools brought together in one experience.</p>
          <div className="hero-actions">
            <a href={playStoreUrl} target="_blank" rel="noreferrer" className="button button-dark" data-testid="oramy-hero-cta-ios">Get ORLIS <ArrowUpRight size={17} /></a>
            <a className="button button-ghost" href="#product" data-testid="oramy-hero-cta-web">Explore ORLIS <ArrowDownRight size={17} /></a>
          </div>
          <div className="hero-footnote"><span>Built for the way you move</span><span className="mono-label">ORLIS / 01</span></div>
        </div>
        <div className="hero-visual" data-reveal>
          <div className="hero-visual-label"><span>REAL APP / SCREEN 01</span><span>AI CHAT</span></div>
          <div className="hero-device-stage">
            <div className="device-spotlight" aria-hidden="true" />
            <PhoneFrame screen={screen("home-chat")} size="hero" testId="oramy-hero-device-frame" onOpen={() => onOpen(screen("home-chat"))} />
            <div className="hero-orbit hero-orbit-one" aria-hidden="true"><span>01</span></div>
            <div className="hero-orbit hero-orbit-two" aria-hidden="true"><span>AI</span></div>
          </div>
          <div className="hero-caption"><span>Ask anything</span><ArrowUpRight size={16} /></div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["ONE AI / MANY DIRECTIONS", "REAL PRODUCT / REAL WORK", "STUDY · CREATE · WORK", "INDIA KA APNA AI APP", "DEPTH WHEN IT MATTERS"];
  return (
    <div className="marquee" data-testid="oramy-marquee-ribbon" aria-label="ORLIS product highlights">
      <div className="marquee-track">{[...items, ...items].map((item, index) => <span key={`${item}-${index}`}>{item}<b>·</b></span>)}</div>
    </div>
  );
}

function ChatSection({ onOpen }) {
  return (
    <section className="story-section chat-section" id="product">
      <div className="page-width split-layout split-chat">
        <SectionIntro number="01" label="AI CHAT / THE STARTING POINT" title={["Start with a thought.", "Go anywhere."]} body="ORLIS brings a calm, capable chat space to the everyday questions, ideas and plans that keep moving." testId="oramy-chat-heading" />
        <div className="chat-panel" data-reveal>
          <div className="panel-topline"><span>REAL APP / AI CHAT</span><span>01 / 13</span></div>
          <PhoneFrame screen={screen("home-chat")} size="tall" testId="oramy-chat-device-frame" onOpen={() => onOpen(screen("home-chat"))} />
          <div className="panel-note"><Sparkles size={16} /><span>One place for the next question.</span></div>
        </div>
      </div>
    </section>
  );
}

function ModelsSection({ onOpen }) {
  const items = ["Fast responses", "Reasoning", "Coding", "Vision", "Multimodal AI"];
  return (
    <section className="story-section models-section" id="models">
      <div className="page-width">
        <SectionIntro number="02" label="AI MODELS / CHOOSE YOUR MODE" title={["The right AI", "for the right task."]} body="Move between different ways of thinking without leaving the ORLIS experience." testId="oramy-models-heading" />
        <div className="models-layout">
          <div className="model-list" data-reveal>
            <div className="model-list-head"><span>YOUR TOOLKIT</span><Layers3 size={17} /></div>
            {items.map((item, index) => <div className="model-row" key={item}><span>0{index + 1}</span><strong>{item}</strong><ArrowUpRight size={15} /></div>)}
            <p className="small-note">Choose based on what you are trying to do, not just what is newest.</p>
          </div>
          <PhonePair screens={[screen("models-selected"), screen("models-list")]} onOpen={onOpen} testPrefix="oramy-models-device" className="models-phones" />
        </div>
      </div>
    </section>
  );
}

function DepthViewSection() {
  return (
    <section className="story-section depth-section" id="depthview" data-testid="oramy-depthview-section">
      <div className="depth-backdrop" aria-hidden="true" />
      <div className="page-width depth-grid">
        <div className="depth-copy" data-reveal>
          <div className="section-index"><span>03</span><span>DEPTHVIEW / WHEN IT MATTERS</span></div>
          <MaskedTitle lines={["Go deeper with", "DepthView."]} />
          <p>Turn a normal question into a deeper, more structured AI exploration when the task needs more than a quick answer.</p>
          <div className="depth-tags"><span>Deeper understanding</span><span>Research</span><span>Complex questions</span><span>Reasoning</span></div>
        </div>
        <div className="depth-art" data-reveal>
          <div className="depth-art-core"><span>DEPTH</span><strong>03</strong><small>Ask past the obvious.</small></div>
          <div className="depth-art-line line-a" /><div className="depth-art-line line-b" /><div className="depth-art-dot dot-a" /><div className="depth-art-dot dot-b" />
        </div>
      </div>
    </section>
  );
}

function RivaSection({ onOpen }) {
  return (
    <section className="story-section riva-section" id="riva">
      <div className="page-width split-layout split-riva">
        <div className="riva-visual" data-reveal>
          <div className="voice-aura" aria-hidden="true" />
          <PhoneFrame screen={screen("riva")} size="tall" testId="oramy-riva-device-frame" onOpen={() => onOpen(screen("riva"))} />
        </div>
        <SectionIntro number="04" label="RIVA / VOICE CONVERSATION" title={["Talk naturally", "with RIVA."]} body="Switch from typing to a live AI conversation when speaking is easier. The real app screen keeps language and voice selection close at hand." testId="oramy-riva-heading" />
      </div>
    </section>
  );
}

function RadarSection({ onOpen }) {
  return (
    <section className="story-section radar-section" id="radar">
      <div className="page-width split-layout split-radar">
        <SectionIntro number="05" label="RADAR / STAY CURIOUS" title={["Discover with", "Radar."]} body="Explore useful and timely information through ORLIS without leaving your AI workspace." testId="oramy-radar-heading" />
        <div className="radar-card" data-reveal>
          <div className="radar-card-top"><span><RadarIcon size={16} /> REAL APP / RADAR</span><span>05 / 13</span></div>
          <PhoneFrame screen={screen("radar")} size="tall" testId="oramy-radar-device-frame" onOpen={() => onOpen(screen("radar"))} />
        </div>
      </div>
    </section>
  );
}

const worldData = {
  study: {
    id: "study", number: "06", label: "STUDY HUB / LEARN IN CONTEXT", title: ["An AI workspace", "built for learning."], body: "Study tools that help you understand, practise and keep your progress in view.", icon: GraduationCap, tone: "blue", screens: ["study-hub", "study-progress"], prefix: "oramy-study-device", features: ["Generate Quiz", "Mock Viva", "Universal Chat", "Study Session", "Flashcards", "Notes", "Homework", "Revision", "Career Guidance", "Mind Maps", "Study Planner", "YouTube AI"],
  },
  create: {
    id: "create", number: "07", label: "CREATOR HUB / MAKE THE NEXT THING", title: ["From idea", "to content."], body: "Turn a first spark into a script, visual, video, or a plan to make it travel further.", icon: Palette, tone: "violet", screens: ["creator-content", "creator-overview"], prefix: "oramy-creator-device", features: ["Script Generator", "Hook Generator", "Content Ideas", "Content Repurposer", "Thumbnail Generator", "Thumbnail Analyzer", "Generate Video", "AI Video Analysis", "SEO / Optimizer", "Content Planner"],
  },
  work: {
    id: "work", number: "08", label: "PROFESSIONAL HUB / GET IT DONE", title: ["AI that works", "with you."], body: "Bring documents, writing, research, planning and coding into one focused workbench.", icon: BriefcaseBusiness, tone: "cyan", screens: ["professional-documents", "professional-overview"], prefix: "oramy-professional-device", features: ["Document Generator", "Document Analysis", "Professional Writing", "Email Writer", "Resume / CV", "Meeting Summary", "Business Proposal", "Report", "Research Assistant", "Excel / Table Analysis", "Business / Planning", "Coding"],
  },
};

function WorldSection({ data, onOpen }) {
  return (
    <section className={`story-section world-section world-${data.id}`} id={data.id}>
      <div className="page-width world-layout">
        <div className="world-copy" data-reveal>
          <div className="world-kicker"><IconMark icon={data.icon} tone={data.tone} /><span>{data.label}</span></div>
          <MaskedTitle lines={data.title} testId={`oramy-${data.id}-heading`} />
          <p>{data.body}</p>
          <div className="feature-cloud">{data.features.map((feature) => <span key={feature}>{feature}</span>)}</div>
        </div>
        <PhonePair screens={data.screens.map(screen)} onOpen={onOpen} testPrefix={data.prefix} className="world-phones" />
      </div>
    </section>
  );
}

function WorldsSection({ onOpen }) {
  return (
    <section className="story-section worlds-section" id="worlds">
      <div className="page-width worlds-layout">
        <div className="worlds-copy" data-reveal>
          <div className="section-index"><span>09</span><span>THE ORLIS MAP</span></div>
          <MaskedTitle lines={["One app.", "Three powerful worlds."]} testId="oramy-worlds-heading" />
          <p>The universal ORLIS assistant connects the space where you learn, the space where you create and the space where you work.</p>
          <div className="world-legend">
            <div><GraduationCap size={17} /><span><strong>Study Hub</strong>Learn, understand and revise.</span></div>
            <div><Palette size={17} /><span><strong>Creator Hub</strong>Create, analyze and optimize.</span></div>
            <div><BriefcaseBusiness size={17} /><span><strong>Professional Hub</strong>Write, research, analyze and work.</span></div>
          </div>
        </div>
        <div className="worlds-device" data-reveal><PhoneFrame screen={screen("navigation")} size="tall" testId="oramy-worlds-device-frame" onOpen={() => onOpen(screen("navigation"))} /></div>
      </div>
    </section>
  );
}

function SupportSection({ onOpen }) {
  return (
    <section className="story-section support-section" id="support">
      <div className="page-width support-layout">
        <div className="support-visual" data-reveal><PhoneFrame screen={screen("support")} size="tall" testId="oramy-support-device-frame" onOpen={() => onOpen(screen("support"))} /></div>
        <div className="support-copy" data-reveal>
          <div className="section-index"><span>10</span><span>HELP & SUPPORT / A CLEAR CHANNEL</span></div>
          <MaskedTitle lines={["Support when", "you need it."]} testId="oramy-support-heading" />
          <p>Open an in-app ticket for questions, bugs, payments, accounts or features. You will see updates in My Tickets.</p>
          <div className="support-points"><span><LifeBuoy size={17} /> Questions</span><span><ShieldCheck size={17} /> Accounts</span><span><CircleDot size={17} /> Features</span></div>
        </div>
      </div>
    </section>
  );
}

const galleryFilters = [["all", "All screens"], ["core", "Core"], ["voice", "Voice"], ["study", "Study"], ["create", "Create"], ["work", "Work"], ["worlds", "Worlds"], ["support", "Support"], ["radar", "Radar"]];

function GallerySection({ onOpen }) {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all" ? screenshots : screenshots.filter((item) => item.category === filter);
  return (
    <section className="story-section gallery-section" id="experience" data-testid="oramy-screen-gallery-dock">
      <div className="page-width">
        <div className="gallery-heading" data-reveal>
          <div><div className="section-index"><span>11</span><span>THE SCREEN ARCHIVE</span></div><MaskedTitle lines={["Explore ORLIS", "in action."]} testId="oramy-gallery-heading" /></div>
          <p>Every screen below is a real screenshot from the ORLIS app, kept authentic and framed for a closer look.</p>
        </div>
        <div className="gallery-filters" data-reveal role="tablist" aria-label="Filter ORLIS screenshots">
          {galleryFilters.map(([id, label]) => <button type="button" role="tab" aria-selected={filter === id} className={filter === id ? "is-active" : ""} data-testid={`oramy-gallery-filter-${id}`} key={id} onClick={() => setFilter(id)}>{label}</button>)}
        </div>
        <div className="gallery-grid" data-testid="oramy-gallery-grid">
          {visible.map((item, index) => (
            <button type="button" className={`gallery-card gallery-card-${index % 3}`} key={item.id} data-testid={`oramy-gallery-slide-${item.id}`} onClick={() => onOpen(item)}>
              <span className="gallery-image-wrap"><img src={item.src} alt={`${item.name} real ORLIS app screenshot`} loading="lazy" data-testid={`oramy-gallery-slide-${item.id}-screen`} /></span>
              <span className="gallery-card-meta"><span><b>{item.section}</b><small>{item.name}</small></span><ExternalLink size={16} /></span>
            </button>
          ))}
        </div>
        <div className="gallery-foot" data-testid="oramy-gallery-count"><span>{visible.length} real screens</span><span>Tap any frame to open the original image</span></div>
      </div>
    </section>
  );
}

function PricingSection({ onGetOrlis }) {
  return (
    <section className="story-section pricing-section" id="pricing">
      <div className="page-width">
        <div className="pricing-heading" data-reveal><div className="section-index"><span>12</span><span>PRICING / MONTHLY ONLY</span></div><MaskedTitle lines={["Pick your", "pace."]} testId="oramy-pricing-heading" /><p>Five simple monthly plans. Final allowances and entitlements can be added when the commercial details are ready.</p></div>
        <div className="pricing-grid">
          {pricingPlans.map((plan, index) => <article className={`pricing-card ${index === 3 ? "is-featured" : ""}`} key={plan.id} data-testid={`oramy-plan-${plan.id}`}><div className="pricing-card-top"><span>0{index + 1}</span>{index === 3 && <span className="plan-tag">Popular</span>}</div><h3>{plan.name}</h3><div className="price" data-testid={`oramy-price-${plan.id}`}>{plan.price}<small>/ month</small></div><p>{plan.note}</p><button type="button" className={index === 3 ? "button button-dark button-full" : "button button-light button-full"} data-testid={`oramy-plan-${plan.id}-button`} onClick={onGetOrlis}>View plan <ArrowUpRight size={15} /></button></article>)}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="story-section faq-section" id="faq" data-testid="oramy-faq-accordion">
      <div className="page-width faq-layout">
        <div className="faq-intro" data-reveal><div className="section-index"><span>13</span><span>FAQ / BEFORE YOU BEGIN</span></div><MaskedTitle lines={["Questions worth", "asking."]} testId="oramy-faq-heading" /><p>A clear starting point for the things people usually want to know.</p></div>
        <div className="faq-list" data-reveal>{faqItems.map(([question, answer], index) => <div className={`faq-item ${open === index ? "is-open" : ""}`} key={question}><button type="button" className="faq-trigger" data-testid={`oramy-faq-item-${index + 1}`} aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)}><span>{question}</span><ChevronDown size={19} /></button>{open === index && <div className="faq-answer"><p>{answer}</p></div>}</div>)}</div>
      </div>
    </section>
  );
}

function Lightbox({ item, onClose }) {
  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = previous; };
  }, [onClose]);
  return (
    <div className="lightbox-backdrop" role="dialog" aria-modal="true" aria-label={`${item.name} original screenshot`} data-testid="oramy-lightbox">
      <div className="lightbox-panel">
        <div className="lightbox-head"><div><span>ORIGINAL APP SCREENSHOT</span><strong>{item.name}</strong></div><button type="button" data-testid="oramy-lightbox-close" className="lightbox-close" onClick={onClose} aria-label="Close screenshot"><X size={21} /></button></div>
        <div className="lightbox-image"><img src={item.src} alt={`${item.name} original ORLIS app screenshot`} data-testid="oramy-lightbox-image" /></div>
        <p>{item.caption}</p>
      </div>
    </div>
  );
}

function LegalContactBlock({ type }) {
  return (
    <div className="contact-block" data-testid={`oramy-${type}-contact-details`}>
      <span className="mono-label">CONTACT / ORLIS AI</span>
      <div className="legal-contact-lines">
        <p><strong>Legal Owner:</strong> Sohail Abass Shah</p>
        <p><strong>Founder:</strong> Massaid Shah</p>
        <p><strong>Email:</strong> <a href="mailto:massaidshah3@gmail.com" data-testid={`oramy-${type}-email`}>massaidshah3@gmail.com</a></p>
        <p><strong>Phone:</strong> <a href="tel:+916006455295" data-testid={`oramy-${type}-phone-primary`}>+91 6006455295</a> / <a href="tel:+919149815646" data-testid={`oramy-${type}-phone-secondary`}>+91 9149815646</a></p>
        <p><strong>WhatsApp:</strong> <a href="https://wa.me/916006455295" target="_blank" rel="noreferrer" data-testid={`oramy-${type}-whatsapp-primary`}>+91 6006455295</a> / <a href="https://wa.me/919149815646" target="_blank" rel="noreferrer" data-testid={`oramy-${type}-whatsapp-secondary`}>+91 9149815646</a></p>
        <p><strong>Website:</strong> <a href="https://oramy.app" target="_blank" rel="noreferrer" data-testid={`oramy-${type}-website`}>oramy.app</a></p>
      </div>
    </div>
  );
}

function PageMeta({ title, description }) {
  useEffect(() => {
    const descriptionNode = document.querySelector('meta[name="description"]');
    const previousTitle = document.title;
    const previousDescription = descriptionNode?.getAttribute("content");
    document.title = title;
    descriptionNode?.setAttribute("content", description);
    return () => {
      document.title = previousTitle;
      if (descriptionNode && previousDescription) descriptionNode.setAttribute("content", previousDescription);
    };
  }, [title, description]);
  return null;
}

function SiteFooter() {
  return (
    <footer className="site-footer" data-testid="oramy-footer-container">
      <div className="page-width footer-top"><div className="footer-brand"><OrlisLogo size="large" testId="oramy-footer-brand-logo" /><p>Chat, learn, create and get work done in one experience.</p></div><div className="footer-links"><span className="mono-label">EXPLORE</span><a href="/#product">Product</a><a href="/#study">Study</a><a href="/#create">Create</a><a href="/#work">Work</a><a href="/#pricing">Pricing</a><a href="/#faq">FAQ</a></div><div className="footer-links"><span className="mono-label">LEGAL & SUPPORT</span><Link to="/privacy-policy" data-testid="oramy-footer-privacy">Privacy Policy</Link><Link to="/terms" data-testid="oramy-footer-terms">Terms & Conditions</Link><Link to="/account-deletion" data-testid="oramy-footer-account-deletion">Account Deletion</Link><a href={playStoreUrl} target="_blank" rel="noreferrer" data-testid="oramy-footer-playstore-link">Get on Google Play</a><a href="/#support" data-testid="oramy-footer-support">Support</a><a href="mailto:massaidshah3@gmail.com" data-testid="oramy-footer-email">Email us</a></div></div>
      <div className="page-width footer-bottom"><span>© 2026 ORLIS AI. All rights reserved.</span><span className="footer-origin">Made with intent from the Himalayas <span aria-hidden="true">↗</span></span></div>
    </footer>
  );
}

function PageFrame({ children }) {
  const [notice, setNotice] = useState("");
  const notify = () => setNotice("ORLIS access destination will be connected here soon.");
  useEffect(() => { if (!notice) return undefined; const timeout = window.setTimeout(() => setNotice(""), 3600); return () => window.clearTimeout(timeout); }, [notice]);
  const content = typeof children === "function" ? children({ notify }) : children;
  return <div className="site-root"><SiteHeader />{content}<SiteFooter /><a className="playstore-float" href={playStoreUrl} target="_blank" rel="noreferrer" aria-label="Open ORLIS AI on Google Play" data-testid="oramy-floating-playstore-link"><span className="playstore-float-badge"><img src="/google-play-icon.svg" alt="Google Play" data-testid="google-play-original-icon" /></span><span className="playstore-float-label">Get app</span></a><div className={`site-toast ${notice ? "is-visible" : ""}`} role="status" data-testid="oramy-cta-status">{notice}</div></div>;
}

function AppHome() {
  const [lightboxScreen, setLightboxScreen] = useState(null);
  const closeLightbox = () => setLightboxScreen(null);
  useEffect(() => { const onKey = (event) => event.key === "Escape" && closeLightbox(); document.addEventListener("keydown", onKey); return () => document.removeEventListener("keydown", onKey); }, []);
  return (
    <PageFrame>{({ notify }) => <>
      <main id="main-content">
        <Hero onOpen={setLightboxScreen} />
        <Marquee />
        <ChatSection onOpen={setLightboxScreen} />
        <ModelsSection onOpen={setLightboxScreen} />
        <DepthViewSection />
        <RivaSection onOpen={setLightboxScreen} />
        <RadarSection onOpen={setLightboxScreen} />
        <WorldSection data={worldData.study} onOpen={setLightboxScreen} />
        <WorldSection data={worldData.create} onOpen={setLightboxScreen} />
        <WorldSection data={worldData.work} onOpen={setLightboxScreen} />
        <WorldsSection onOpen={setLightboxScreen} />
        <SupportSection onOpen={setLightboxScreen} />
        <GallerySection onOpen={setLightboxScreen} />
        <PricingSection onGetOrlis={notify} />
        <FaqSection />
      </main>
      {lightboxScreen && <Lightbox item={lightboxScreen} onClose={closeLightbox} />}
    </>}</PageFrame>
  );
}

const privacySections = [
  {
    id: "introduction",
    heading: "Introduction",
    paragraphs: [
      "This Privacy Policy explains how ORLIS AI may process information when you use the ORLIS AI mobile application, package/application ID com.oramy.ai, and related support or account experiences.",
      "ORLIS AI combines AI chat, multiple AI models, image generation, file and document processing, Riva Live AI Voice, DepthView, Study Hub, Creator Hub, Professional Hub, Radar/news discovery, accounts and paid plans. The exact data practices for a feature can depend on how that feature is implemented and which model or service you select.",
      "Last Updated: September 5, 2026 · Contact: massaidshah3@gmail.com",
    ],
  },
  {
    id: "information-you-provide",
    heading: "Information you provide",
    paragraphs: ["Depending on how you use ORLIS AI, you may provide the following categories of information:"],
    bullets: [
      "Profile and account information, such as your name or other profile information where provided.",
      "Email address, authentication identifiers, account ID or other internal account identifiers.",
      "Subscription, plan, usage, credit, entitlement and account-setting information.",
      "Text prompts, questions, chat messages and other instructions sent to AI features.",
      "Images, photos, documents, files, study material, creator inputs and professional or work-related content.",
      "Voice or audio input when you choose to use Riva Live AI Voice.",
    ],
  },
  {
    id: "account-authentication",
    heading: "Account and authentication information",
    paragraphs: [
      "ORLIS AI supports user accounts and authentication. Account-related information may include your email address, authentication identifiers, account ID, plan status, usage or entitlement information, settings and preferences where applicable.",
      "ORLIS AI also supports Google Sign-In. Authentication is handled through the relevant authentication mechanism. ORLIS AI does not state that it receives or stores your Google password.",
    ],
  },
  {
    id: "ai-prompts-conversations",
    heading: "AI prompts and conversations",
    paragraphs: [
      "When you communicate with an AI model through ORLIS AI, your prompts, questions, messages and any information needed to fulfil the request may be processed to generate a result and provide the selected feature.",
      "Do not voluntarily submit highly sensitive, confidential or regulated information unless doing so is necessary and appropriate for the task. AI outputs can be inaccurate, incomplete or inappropriate, and your input may be processed by a relevant service provider as described below.",
      "ORLIS AI does not claim that it permanently stores every prompt, response or conversation. Exact history, retention and deletion behaviour: Retention and handling can vary by feature and service provider; ORLIS does not promise a fixed period.",
    ],
  },
  {
    id: "uploaded-content",
    heading: "Uploaded images, documents and files",
    paragraphs: [
      "ORLIS AI lets you provide images, photos, documents and other supported files to AI features. These files may be processed by ORLIS AI and/or the relevant service provider to understand an image, answer questions, assist with study, complete document-related tasks, support creator tools or support professional tools.",
      "ORLIS AI does not claim that every uploaded file is permanently retained. File storage, retention and deletion behaviour: Retention and handling can vary by feature and service provider; ORLIS does not promise a fixed period. You should only upload content that you have the right to provide and that is appropriate for the requested task.",
    ],
  },
  {
    id: "riva-voice-audio",
    heading: "Voice and audio processing for Riva",
    paragraphs: [
      "Riva is ORLIS AI’s live conversational AI voice experience. If you use Riva, the app may request microphone access and may process live or streamed audio so that your speech can be understood and an AI-generated audio response can be produced.",
      "Voice functionality can involve audio being sent to a relevant AI or infrastructure provider for processing. ORLIS AI does not claim that raw audio is permanently stored. Audio storage and retention: raw-audio retention can vary by feature and service provider, and ORLIS does not promise permanent storage.",
    ],
  },
  {
    id: "subscriptions-entitlements",
    heading: "Subscriptions, plans and entitlements",
    paragraphs: [
      "ORLIS AI has Free, Basic, Plus, Pro and Max subscription tiers. Depending on the plan, the app may apply different model access, daily usage credits, chat limits, image-generation limits, voice limits and other feature entitlements. These limits and entitlements can change over time.",
      "Paid Android subscriptions are handled through Google Play Billing. ORLIS AI may receive purchase, subscription, plan or entitlement status needed to provide the service. ORLIS AI does not claim that it receives or stores your full credit or debit card details from Google Play.",
    ],
  },
  {
    id: "technical-diagnostic-information",
    heading: "Technical and diagnostic information",
    paragraphs: [
      "ORLIS AI may process technical information necessary to operate, secure and troubleshoot the service. Depending on the implementation, this may include device or app information, operating system or app version, diagnostic and error information, authentication or session information, service usage information and security or abuse-prevention information.",
      "ORLIS AI does not claim to intentionally collect advertising ID, precise location, contacts, SMS, call logs or other sensitive device categories based on the information supplied for this website. Any additional technical, analytics or tracking categories: Retention and handling can vary by feature and service provider; ORLIS does not promise a fixed period.",
    ],
  },
  {
    id: "how-information-is-used",
    heading: "How information is used",
    paragraphs: ["Information may be used, where applicable, to:"],
    bullets: [
      "Create and manage your ORLIS AI account and authenticate you.",
      "Provide AI chat, model, image, file, document, voice, study, creator, professional and Radar features you request.",
      "Calculate or enforce plan limits, credits, usage and entitlements.",
      "Operate, secure, troubleshoot and improve the service.",
      "Respond to support requests, prevent abuse and protect users and the service.",
      "Process subscriptions and maintain purchase-related account status.",
    ],
  },
  {
    id: "ai-processing",
    heading: "AI processing",
    paragraphs: [
      "Information required to fulfil a request may be processed by the relevant third-party AI or model service depending on the feature and AI model you select. ORLIS AI does not claim that every prompt, file or voice interaction is sent to every provider.",
      "ORLIS AI may provide access to models or services from providers such as OpenAI, Google/Gemini and Anthropic/Claude. ORLIS AI does not own those third-party models. Provider processing, storage and retention may be governed by the provider’s terms and policies, which should be reviewed where relevant.",
    ],
  },
  {
    id: "third-party-processing-sharing",
    heading: "Third-party service providers and data sharing",
    paragraphs: [
      "ORLIS AI may share or make information available to service providers when needed to provide a feature you request, process a subscription, operate infrastructure, support authentication, prevent abuse or comply with a legal obligation.",
      "Relevant providers may include AI/model providers, Google Sign-In, Google Play Billing, hosting or infrastructure providers and support or security tools selected by ORLIS AI. The information shared depends on the feature you use and the provider needed to fulfil your request.",
      "ORLIS AI does not state that it sells personal data. Provider terms may apply, and no unsupported certification or provider retention guarantee is promised.",
    ],
  },
  {
    id: "google-sign-in",
    heading: "Google Sign-In",
    paragraphs: ["If you choose Google Sign-In, the relevant authentication mechanism may provide ORLIS AI with the account information needed to authenticate and manage your ORLIS AI account. ORLIS AI does not state that it receives or stores your Google password. Review Google’s own policies for Google account processing."],
  },
  {
    id: "google-play-billing",
    heading: "Google Play Billing",
    paragraphs: ["Android subscriptions are purchased and managed through Google Play Billing. Google Play may process payment information and purchase details under its own terms. ORLIS AI may receive the subscription or entitlement status needed to provide the plan. Current prices and applicable purchase terms are shown in the in-app purchase screen and Google Play interface."],
  },
  {
    id: "retention-deletion",
    heading: "Data retention and deletion",
    paragraphs: [
      "ORLIS AI keeps information only for as long as needed for the applicable purpose, feature, account, security or legal requirement. Retention can vary by feature and service provider, and no specific retention period is promised.",
      "Account deletion instructions are available at /account-deletion. When a deletion request is completed, ORLIS removes associated account data subject to limited records that may need to be retained for legal, security or transaction purposes.",
    ],
  },
  {
    id: "security",
    heading: "Security",
    paragraphs: ["ORLIS AI uses reasonable measures appropriate to the service to protect information. No method of transmission, storage or processing can be promised to be absolutely secure. Security practices can vary as the service develops."],
  },
  {
    id: "choices-rights",
    heading: "User choices and rights",
    paragraphs: [
      "Depending on where you live, you may have rights concerning access, correction, deletion, restriction, objection or portability of personal information. The rights available and the request process depend on applicable law and the final ORLIS AI entity and service setup.",
      "To make a request, email massaidshah3@gmail.com. ORLIS may need to verify enough information to identify the account and respond to the request.",
    ],
  },
  {
    id: "children-minors",
    heading: "Children and minors",
    paragraphs: ["ORLIS AI does not state a specific minimum age on this website. Use the service only where legally permitted and follow any age or guardian requirements that apply to you."],
  },
  {
    id: "international-processing",
    heading: "International and service-provider processing",
    paragraphs: ["AI, authentication, billing, infrastructure and support providers may process information in locations different from where you live, depending on the services selected by ORLIS AI. ORLIS AI does not promise that information will remain in your country."],
  },
  {
    id: "policy-changes",
    heading: "Changes to this Privacy Policy",
    paragraphs: ["ORLIS AI may update this Privacy Policy when the service, providers or applicable requirements change. The Last Updated date at the top of this page will show when the current version was published."],
  },
  {
    id: "privacy-contact",
    heading: "Contact",
    paragraphs: ["Privacy requests: massaidshah3@gmail.com. Legal Owner: Sohail Abass Shah. Founder: Massaid Shah. Phone: +91 6006455295 / +91 9149815646. WhatsApp: +91 6006455295 / +91 9149815646."],
  },
];

const termsSections = [
  { id: "acceptance", heading: "Acceptance of Terms", paragraphs: ["These Terms & Conditions govern your use of ORLIS AI, package/application ID com.oramy.ai, and related services. By using ORLIS AI, you agree to these Terms as updated from time to time. Last Updated: September 5, 2026."] },
  { id: "description", heading: "Description of ORLIS AI", paragraphs: ["ORLIS AI is an all-in-one AI application that may provide AI chat, multiple AI models, image generation, uploads for AI processing, Riva Live AI Voice, DepthView, Study Hub, Creator Hub, Professional Hub, Radar/news discovery, accounts and subscription plans. Features labelled Coming Soon, including possible video-generation features, are not promised as available production features."] },
  { id: "eligibility", heading: "Eligibility", paragraphs: ["The minimum age and intended audience for ORLIS AI have not been supplied to this website project. ORLIS does not state a specific minimum age in this website. Use the service only where legally permitted and follow any age or guardian requirements that apply to you. Do not use ORLIS AI if you are not legally permitted to agree to these Terms in your location."] },
  { id: "accounts-security", heading: "Accounts and account security", paragraphs: ["Some features require an account. You are responsible for providing information that is accurate enough to manage your account, protecting your authentication details and promptly reporting suspected unauthorized access. Google Sign-In may be available; ORLIS AI does not receive or store your Google password through that mechanism."] },
  { id: "acceptable-use", heading: "Acceptable use", paragraphs: ["You may use ORLIS AI only lawfully and in a way that does not harm the service or other people. Do not use it to violate law or rights, submit malware, attempt unauthorized access, evade plan limits, disrupt providers, abuse model services, generate or distribute harmful material, or use output to make high-impact decisions without appropriate human review."] },
  { id: "ai-content", heading: "AI-generated content and accuracy", paragraphs: ["Generative AI can produce inaccurate, incomplete, outdated, biased or inappropriate results. You are responsible for evaluating output before relying on, submitting, publishing or distributing it. ORLIS AI output is not a substitute for qualified medical, legal, financial, educational, employment, safety or other professional advice where professional advice is required."] },
  { id: "user-responsibility", heading: "Your responsibility", paragraphs: ["You are responsible for your prompts, uploaded materials, account activity and use of results. Do not submit highly sensitive or confidential information unless necessary and appropriate. Review important results against reliable sources and obtain qualified advice where needed."] },
  { id: "user-content", heading: "Uploaded and user-provided content", paragraphs: ["You retain your rights in original documents, photos, prompts, files, audio and other content that you provide, subject to rights belonging to other people. You grant ORLIS AI the limited permission needed to host, transmit, process and return that content to provide the feature you request. ORLIS AI does not claim ownership of your original content merely because you upload it."] },
  { id: "intellectual-property", heading: "Intellectual property", paragraphs: ["ORLIS AI branding, original application interface, website content, code and other owned materials remain protected by applicable intellectual-property laws, subject to third-party materials and rights. OpenAI, Google, Gemini, Anthropic, Claude and other names, models and trademarks belong to their respective owners. No ownership of those third-party materials is claimed."] },
  { id: "third-party-ai", heading: "Third-party AI and other services", paragraphs: ["Depending on the feature or model selected, information required to fulfil your request may be processed by a relevant provider such as OpenAI, Google/Gemini or Anthropic/Claude. ORLIS AI does not own those models, does not claim every request is sent to every provider and cannot promise that any particular model or provider will remain available permanently. Provider terms may also apply."] },
  { id: "riva", heading: "Riva Live AI Voice", paragraphs: ["Riva may use microphone access, live or streamed audio processing and AI-generated audio responses. You are responsible for using voice features lawfully and for warning other people before recording or processing their voice where required. Raw audio storage and retention: raw-audio retention can vary by feature and service provider, and ORLIS does not promise permanent storage."] },
  { id: "study-hub", heading: "Study Hub", paragraphs: ["Study Hub may include quiz, study-session, flashcard, notes, homework, mind-map, YouTube/content summarization and other educational AI tools. Educational output can contain mistakes and is assistance only; verify important information with appropriate sources or educators."] },
  { id: "creator-hub", heading: "Creator Hub", paragraphs: ["Creator Hub may include script, hook, content-idea, video-analysis, thumbnail and related creator tools. These tools may process user-provided text, images, links, video-related information or other content. You are responsible for rights, permissions, disclosures and review of anything you create or distribute."] },
  { id: "professional-hub", heading: "Professional Hub", paragraphs: ["Professional Hub may include document generation, resume/CV, meeting summary and professional writing/productivity tools. Review professional content before relying on, submitting or distributing it. ORLIS AI does not promise that generated work is complete, accurate or suitable for a particular professional purpose."] },
  { id: "radar", heading: "Radar", paragraphs: ["Radar may surface current stories and discovery content across categories such as technology/science and general/top stories. ORLIS AI does not claim that every story is independently fact-checked or verified. Consult original and reliable sources when accuracy matters."] },
  { id: "subscriptions", heading: "Subscriptions and Google Play Billing", paragraphs: ["ORLIS AI offers Free, Basic, Plus, Pro and Max tiers. Paid Android plans are handled through Google Play Billing. Current prices, plan access and purchase terms appear in the in-app purchase screen and Google Play interface and may change. ORLIS AI does not directly promise to receive or store your full payment-card details from Google Play."] },
  { id: "renewal-cancellation-refunds", heading: "Renewal, cancellation and refunds", paragraphs: ["Paid subscriptions may automatically renew under the applicable Google Play subscription terms until cancelled. You can manage or cancel applicable Android subscriptions through Google Play. Refund eligibility is subject to applicable law and Google Play policies; no automatic refund is promised by these Terms."] },
  { id: "limits-credits", heading: "Plan limits, credits and entitlements", paragraphs: ["Plans may differ in model access, daily usage credits, chat limits, image-generation limits, voice usage limits, premium features and other entitlements. Limits, models, features and availability can change. The current in-app plan and purchase screens control the details shown at the time of use."] },
  { id: "feature-changes", heading: "Changes to features, models and plans", paragraphs: ["ORLIS AI may add, modify, pause or remove features, models, tools, limits and plans. No particular model, feature or availability is guaranteed to remain permanently available. A feature shown as Coming Soon is not a promise that it is currently available."] },
  { id: "prohibited-activities", heading: "Prohibited activities and AI abuse", paragraphs: ["You may not reverse engineer or interfere with ORLIS AI, use automation to overload it, bypass authentication or plan controls, misuse third-party model services, upload content without rights, impersonate others, or use the service for unlawful, abusive, fraudulent or dangerous activity."] },
  { id: "suspension-termination", heading: "Suspension and termination", paragraphs: ["ORLIS AI may restrict, suspend or terminate access where reasonably necessary to protect the service, users, providers or legal rights, including for suspected abuse or violation of these Terms. ORLIS will apply any notice or account-closure process required by applicable law."] },
  { id: "availability", heading: "Service availability", paragraphs: ["ORLIS AI depends on mobile networks, device capabilities, Google Play, AI/model providers and other services. Outages, maintenance, model changes and other events may affect availability. No uptime, response-time or uninterrupted-service guarantee is made."] },
  { id: "disclaimers", heading: "Disclaimers and limitation of liability", paragraphs: ["To the extent permitted by applicable law, ORLIS AI is provided on an as-available basis and without promises beyond those expressly made in these Terms. Nothing here removes consumer rights that cannot legally be excluded. Any warranties, liability limits and legally required exceptions must be interpreted under applicable law."] },
  { id: "indemnity", heading: "Indemnity", paragraphs: ["To the extent permitted by applicable law, you may be responsible for claims arising from your unlawful use of ORLIS AI, violation of these Terms or infringement of another person’s rights. This provision does not expand any obligation or remove rights that cannot legally be limited."] },
  { id: "changes-to-terms", heading: "Changes to these Terms", paragraphs: ["ORLIS AI may update these Terms as the service changes. The Last Updated date at the top of this page will show when the current version was published. Continued use after an effective update may be subject to the revised Terms where permitted by law."] },
  { id: "governing-law", heading: "Governing law and jurisdiction", paragraphs: ["Legal Owner: Sohail Abass Shah. These Terms are governed by applicable law, and mandatory consumer protections remain unaffected. No additional governing-law or jurisdiction choice is stated here."] },
  { id: "terms-contact", heading: "Contact", paragraphs: ["Questions about these Terms: massaidshah3@gmail.com. Legal Owner: Sohail Abass Shah. Founder: Massaid Shah. Phone: +91 6006455295 / +91 9149815646. WhatsApp: +91 6006455295 / +91 9149815646."] },
];

function LegalSection({ section, testPrefix }) {
  return (
    <section className="legal-block" id={section.id} data-testid={`${testPrefix}-${section.id}`}>
      <h2>{section.heading}</h2>
      {section.paragraphs.map((paragraph, index) => <p key={`${section.id}-paragraph-${index}`}>{paragraph}</p>)}
      {section.bullets && <ul className="legal-list">{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
    </section>
  );
}

function LegalOutline({ sections, testPrefix }) {
  return (
    <nav className="legal-outline" aria-label="On this page" data-testid={`${testPrefix}-contents`}>
      <span className="mono-label">ON THIS PAGE</span>
      <div>{sections.map((section, index) => <a href={`#${section.id}`} key={section.id} data-testid={`${testPrefix}-contents-${index + 1}`}>{String(index + 1).padStart(2, "0")} {section.heading}</a>)}</div>
    </nav>
  );
}

function LegalReviewCard({ type }) {
  return (
    <div className="legal-facts-card" data-testid={`oramy-${type}-published-details`}>
      <span className="mono-label">ORLIS AI / PUBLISHED DETAILS</span>
      <ul className="legal-list legal-list-compact">
        <li>Last Updated: September 5, 2026</li>
        <li>Legal Owner: Sohail Abass Shah</li>
        <li>Founder: Massaid Shah</li>
        <li>Android package: com.oramy.ai</li>
        <li>Contact: massaidshah3@gmail.com</li>
      </ul>
    </div>
  );
}

function LegalPage({ type }) {
  const isPrivacy = type === "privacy";
  const sections = isPrivacy ? privacySections : termsSections;
  const title = isPrivacy ? "Privacy Policy | ORLIS AI" : "Terms & Conditions | ORLIS AI";
  const description = isPrivacy ? "Privacy Policy for ORLIS AI, including accounts, AI processing, Riva voice, uploads, subscriptions and user choices." : "Terms & Conditions for using ORLIS AI, including AI limits, user content, Google Play subscriptions and account responsibilities.";
  const prefix = isPrivacy ? "oramy-privacy" : "oramy-terms";
  return (
    <PageFrame>
      <main className="legal-page" data-testid={`${prefix}-page`}>
        <PageMeta title={title} description={description} />
        <div className="page-width legal-hero" data-reveal>
          <div className="section-index"><span>{isPrivacy ? "P / 01" : "T / 01"}</span><span>ORLIS AI / {isPrivacy ? "PRIVACY POLICY" : "TERMS & CONDITIONS"}</span></div>
          <MaskedTitle as="h1" lines={isPrivacy ? ["Privacy,", "in plain language."] : ["Terms for", "using ORLIS AI."]} testId={`${prefix}-title`} />
          <p>{isPrivacy ? "This Privacy Policy explains the information ORLIS AI may process when you use its account, AI, voice, upload and subscription features." : "These Terms explain how you may use ORLIS AI, including model limitations, user responsibilities and Google Play subscriptions."}</p>
          <div className="legal-meta"><span>Last Updated: September 5, 2026</span></div>
        </div>
        <div className="page-width legal-layout">
          <div className="legal-copy"><LegalOutline sections={sections} testPrefix={prefix} />{sections.map((section) => <LegalSection key={section.id} section={section} testPrefix={prefix} />)}</div>
          <aside className="legal-aside"><LegalReviewCard type={isPrivacy ? "privacy" : "terms"} /><LegalContactBlock type={isPrivacy ? "privacy" : "terms"} /></aside>
        </div>
      </main>
    </PageFrame>
  );
}

function AccountDeletionPage() {
  const title = "Account Deletion | ORLIS AI";
  const description = "Public account deletion instructions for ORLIS AI, package com.oramy.ai.";
  return (
    <PageFrame>
      <main className="legal-page deletion-page" data-testid="oramy-account-deletion-page">
        <PageMeta title={title} description={description} />
        <div className="page-width legal-hero" data-reveal>
          <div className="section-index"><span>A / 01</span><span>ORLIS AI / ACCOUNT DELETION</span></div>
          <MaskedTitle as="h1" lines={["Delete your", "ORLIS account."]} testId="oramy-account-deletion-title" />
          <p>This public page explains the account-deletion information currently available for ORLIS AI, Android package/application ID com.oramy.ai. You do not need to log in to read it.</p>
          <div className="legal-meta"><span>APP PACKAGE: com.oramy.ai</span><span>METHOD: EMAIL REQUEST</span></div>
        </div>
        <div className="page-width legal-layout">
          <div className="legal-copy">
            <LegalOutline sections={[{ id: "request-deletion", heading: "Request deletion" }, { id: "what-happens", heading: "What happens to data" }, { id: "partial-deletion", heading: "Delete certain data" }, { id: "subscriptions", heading: "Subscriptions" }, { id: "contact", heading: "Contact" }]} testPrefix="oramy-account-deletion" />
            <section className="legal-block" id="request-deletion" data-testid="oramy-account-deletion-request">
              <h2>How to request account deletion</h2>
              <p>To request deletion, email massaidshah3@gmail.com from the email associated with your ORLIS account. Include enough information to identify the account, but do not send a password or unnecessary sensitive information.</p>
              <div className="legal-note" data-testid="oramy-account-deletion-method"><strong>Send your deletion request from the email associated with your ORLIS account.</strong><a href="mailto:massaidshah3@gmail.com" data-testid="oramy-account-deletion-email">massaidshah3@gmail.com</a></div>
              <ol className="legal-step-list"><li>Send the request to massaidshah3@gmail.com from the email associated with the ORLIS account.</li><li>Provide enough account information to identify the account. Do not send a password or unnecessary sensitive information.</li><li>ORLIS AI will review the request and may ask for information needed to protect the account.</li><li>Associated account data will be deleted subject to limited records that may need to be retained for legal, security, fraud-prevention or transaction purposes.</li></ol>
              <p>ORLIS does not promise a specific processing timeframe. Retention and handling can vary by feature and service provider.</p>
            </section>
            <section className="legal-block" id="what-happens" data-testid="oramy-account-deletion-data">
              <h2>What happens to associated data</h2>
              <p>A completed deletion request may cover the ORLIS account profile, authentication identifiers, account settings, prompts or interaction history, uploaded content, usage and entitlement records, subject to what is stored and the final deletion procedure.</p>
              <p>Some limited records may be retained where legally, securely or transactionally required, or where needed to resolve disputes and prevent abuse. Retention can vary by feature and service provider, and no fixed period is promised.</p>
            </section>
            <section className="legal-block" id="partial-deletion" data-testid="oramy-account-deletion-partial">
              <h2>Delete certain data without deleting your account</h2>
              <p>To request removal of particular data without closing your account, email massaidshah3@gmail.com and identify the data or feature involved. ORLIS will review the request and confirm what can be removed.</p>
            </section>
            <section className="legal-block" id="subscriptions" data-testid="oramy-account-deletion-subscriptions">
              <h2>Subscriptions and Google Play</h2>
              <p>Deleting an ORLIS AI account does not automatically promise cancellation of a Google Play subscription. Manage or cancel applicable Android subscriptions through Google Play Billing before requesting deletion, and review Google Play’s current subscription terms.</p>
            </section>
            <section className="legal-block" id="contact" data-testid="oramy-account-deletion-contact">
              <h2>Contact</h2>
              <p>Account-deletion contact: massaidshah3@gmail.com</p>
              <p>Support contact: massaidshah3@gmail.com · Legal owner/entity: Sohail Abass Shah</p>
            </section>
          </div>
          <aside className="legal-aside"><div className="legal-facts-card" data-testid="oramy-account-deletion-support"><span className="mono-label">DELETION SUPPORT</span><p>Send requests from the email associated with your ORLIS account to <a href="mailto:massaidshah3@gmail.com" data-testid="oramy-account-deletion-aside-email">massaidshah3@gmail.com</a>.</p><p>Do not send passwords or unnecessary sensitive information. ORLIS may retain limited records where required for legal, security or transaction purposes.</p></div><LegalContactBlock type="account-deletion" /></aside>
        </div>
      </main>
    </PageFrame>
  );
}
const playStoreDestination = "https://play.google.com/store/apps/details?id=com.oramy.ai";
const campaignKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "wbraid", "gbraid"];

function buildPlayStoreUrl() {
  const destination = new URL(playStoreDestination);
  const query = new URLSearchParams(window.location.search);
  campaignKeys.forEach((key) => {
    const value = query.get(key);
    if (value) destination.searchParams.set(key, value);
  });
  return destination.toString();
}

function DownloadPage() {
  const [isAndroid, setIsAndroid] = useState(false);
  const [storeUrl, setStoreUrl] = useState(playStoreDestination);

  useEffect(() => {
    const android = /Android/i.test(window.navigator.userAgent);
    const destination = buildPlayStoreUrl();
    setIsAndroid(android);
    setStoreUrl(destination);
    if (!android) return undefined;
    const frame = window.requestAnimationFrame(() => window.location.assign(destination));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <PageFrame>
      <main className="download-page" data-testid="oramy-download-page">
        <PageMeta title="Download Orlis AI | ORLIS AI" description="Open Orlis AI on Google Play." />
        <div className="download-card" data-reveal>
          <OrlisLogo size="large" testId="oramy-download-logo" />
          <span className="mono-label">ORLIS AI / ANDROID DOWNLOAD</span>
          <MaskedTitle as="h1" lines={["Open Orlis AI", "on Google Play."]} testId="oramy-download-title" />
          <p>{isAndroid ? "Opening Google Play…" : "Install Orlis AI from the official Google Play listing."}</p>
          <div className="download-status" role="status" data-testid="oramy-download-status">{isAndroid ? "If Google Play does not open automatically, use the button below." : "Available for Android"}</div>
          <a className="button button-dark" href={storeUrl} target="_blank" rel="noreferrer" data-testid="oramy-download-open-playstore">Open Google Play <ArrowUpRight size={17} /></a>
        </div>
      </main>
    </PageFrame>
  );
}


function RouteScrollReset() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    const reset = () => {
      if (hash) {
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "auto", block: "start" });
      } else {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    };
    const frame = window.requestAnimationFrame(() => window.requestAnimationFrame(reset));
    const timeout = window.setTimeout(reset, 180);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      window.history.scrollRestoration = previousRestoration;
    };
  }, [pathname, hash]);
  return null;
}

function App() {
  return <><MotionRuntime /><RouteScrollReset /><Routes><Route path="/" element={<AppHome />} /><Route path="/download" element={<DownloadPage />} /><Route path="/privacy-policy" element={<LegalPage type="privacy" />} /><Route path="/privacy" element={<LegalPage type="privacy" />} /><Route path="/terms" element={<LegalPage type="terms" />} /><Route path="/account-deletion" element={<AccountDeletionPage />} /></Routes></>;
}

export default App;